import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface SearchExport {
  id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  total_records: number;
  total_pages: number;
  pages_completed: number;
  progress_percentage: number;
  is_downloadable: boolean;
  file_name?: string;
  file_size?: number;
  file_size_formatted?: string;
  download_url?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface StartSearchExportResponse {
  export_id: number;
  status: string;
  total_records: number;
  total_pages: number;
  progress_percentage: number;
  created_at: string;
  message: string;
}

export interface StartSearchExportParams {
  search?: string;
  tags?: { [key: string]: string[] } | null;
  archived?: boolean;
  workspace_id?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  date_range?: string;
  // Explicit selection of report IDs to export. When provided and non-empty,
  // only these reports are exported (combined with any active filters via AND).
  search_report_ids?: number[];
}

export interface SearchExportListPagination {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface SearchExportListResponse {
  data: SearchExport[];
  pagination: SearchExportListPagination;
}

export interface SearchExportListParams {
  status?: string;
  page?: number;
  per_page?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SearchExportService {
  private apiUrl = environment.api.url;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHttpOptions(): Observable<{ headers: HttpHeaders }> {
    return this.authService.token().pipe(
      map(token => ({
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
      }))
    );
  }

  startExport(params: StartSearchExportParams): Observable<StartSearchExportResponse> {
    const url = `${this.apiUrl}/search-export/start`;
    const payload: any = {};

    if (params.search) {
      payload.search = params.search;
    }
    if (params.tags) {
      payload.tags = params.tags;
    }
    if (params.archived !== undefined) {
      payload.archived = params.archived;
    }
    if (params.workspace_id) {
      payload.workspace_id = params.workspace_id;
    }
    if (params.sort_by) {
      payload.sort_by = params.sort_by;
    }
    if (params.sort_dir) {
      payload.sort_dir = params.sort_dir;
    }
    if (params.date_range && params.date_range !== 'all') {
      payload.date_range = params.date_range;
    }
    if (params.search_report_ids && params.search_report_ids.length > 0) {
      payload.search_report_ids = params.search_report_ids;
    }

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, payload, options).pipe(
          map(response => {
            const data = response.data || response;
            return {
              export_id: data.export_id,
              status: data.status,
              total_records: data.total_records,
              total_pages: data.total_pages,
              progress_percentage: data.progress_percentage,
              created_at: data.created_at,
              message: response.metadata?.message || 'Export started successfully.'
            };
          }),
          catchError(error => throwError(() => error))
        )
      )
    );
  }

  listExports(params?: SearchExportListParams): Observable<SearchExportListResponse> {
    const url = `${this.apiUrl}/search-export/list`;

    return this.getHttpOptions().pipe(
      switchMap(options => {
        let httpParams = new HttpParams();
        if (params?.status) {
          httpParams = httpParams.set('status', params.status);
        }
        if (params?.page) {
          httpParams = httpParams.set('page', params.page.toString());
        }
        if (params?.per_page) {
          httpParams = httpParams.set('per_page', params.per_page.toString());
        }

        return this.http.get<any>(url, { ...options, params: httpParams }).pipe(
          map(response => {
            const exportsArray = response.data || [];
            const metadata = response.metadata || {};
            return {
              data: exportsArray.map((item: any) => ({
                id: item.id,
                status: item.status,
                total_records: item.total_records,
                total_pages: item.total_pages,
                pages_completed: item.pages_completed,
                progress_percentage: item.progress_percentage,
                is_downloadable: item.is_downloadable,
                file_name: item.file_name,
                file_size: item.file_size,
                file_size_formatted: item.file_size_formatted,
                download_url: item.download_url,
                created_at: item.created_at,
                started_at: item.started_at,
                completed_at: item.completed_at,
                error_message: item.error_message
              })),
              pagination: {
                total: metadata.total || 0,
                per_page: metadata.per_page || 15,
                current_page: metadata.current_page || 1,
                last_page: metadata.last_page || 1
              }
            };
          }),
          catchError(error => throwError(() => error))
        );
      })
    );
  }

  getExportStatus(exportId: number): Observable<SearchExport> {
    const url = `${this.apiUrl}/search-export/${exportId}/status`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, options).pipe(
          map(response => {
            const data = response.data || response;
            return {
              id: data.id,
              status: data.status,
              total_records: data.total_records,
              total_pages: data.total_pages,
              pages_completed: data.pages_completed,
              progress_percentage: data.progress_percentage,
              is_downloadable: data.is_downloadable,
              file_name: data.file_name,
              file_size: data.file_size,
              file_size_formatted: data.file_size_formatted,
              download_url: data.download_url,
              created_at: data.created_at,
              started_at: data.started_at,
              completed_at: data.completed_at,
              error_message: data.error_message
            };
          }),
          catchError(error => throwError(() => error))
        )
      )
    );
  }

  downloadExport(exportId: number): Observable<{ blob: Blob; fileName: string }> {
    const url = `${this.apiUrl}/search-export/${exportId}/download`;

    return this.authService.token().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        return this.http.get(url, {
          headers,
          responseType: 'blob',
          observe: 'response'
        }).pipe(
          map(response => {
            const blob = response.body as Blob;
            const contentDisposition = response.headers.get('Content-Disposition');
            let fileName = `search-report-export-${exportId}.zip`;
            if (contentDisposition) {
              const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
              if (matches && matches[1]) {
                fileName = matches[1].replace(/['"]/g, '');
              }
            }
            return { blob, fileName };
          }),
          catchError(error => throwError(() => error))
        );
      })
    );
  }
}
